import { HttpClient } from '@angular/common/http';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	inject,
	NgZone,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { environment } from '@environment/environment';
import { CurrentUserService } from '@services/auth/current-user.service';
import { ImageLoadQueueService } from '@shared/services/image-load-queue.service';
import { ScrollVisibilityService } from '@shared/services/scroll-visibility.service';
import { SharedModule } from 'primeng/api';
import { ImageModule } from 'primeng/image';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subscription } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

/**
 * Interface to store cache data with timestamp
 */
interface CacheEntry {
	url: string | null;
	timestamp: number;
}

@Component({
	selector: 'app-avatar',
	imports: [SharedModule, ImageModule, TooltipModule, SkeletonModule],
	templateUrl: './avatar.component.html',
	styleUrls: ['./avatar.component.scss'],
})
export class AvatarComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
	@Input() imageUrl?: string;
	@Input() name!: string;
	@Input() extraInfo?: string;
	@Input() size = 48; // Size of the avatar in pixels (default: 48px)
	@Input() showInfo = true; // Show name and extra info next to avatar
	@Input() employeeId?: number; // Optional: if provided, will fetch signed URL (deprecated, use userId)
	@Input() userId?: number; // Optional: if provided, will fetch profile photo from Supabase
	@Input() lazyLoad = true; // Enable lazy loading by default for better performance
	@Input() showTooltip = true; // Show tooltip on hover with the name (enabled by default)
	@Input() enablePreview = true; // Enable image preview/zoom on click (enabled by default)
	@Input() showSkeletonWhileLoading = false; // When true and the photo is being fetched from backend, render a skeleton circle instead of initials until the request resolves.
	@ViewChild('avatarContainer', { static: false }) avatarContainer!: ElementRef<HTMLElement>;

	fullImageUrl?: string;
	photoResolved = false;
	originalImageUrl?: string; // Original image URL without transformations for preview
	private http = inject(HttpClient);
	private ngZone = inject(NgZone);
	private cdr = inject(ChangeDetectorRef);
	private imageLoadQueue = inject(ImageLoadQueueService);
	private scrollVisibilityService = inject(ScrollVisibilityService);
	private currentUserService = inject(CurrentUserService);
	private intersectionObserver?: IntersectionObserver;
	private scrollSubscription?: Subscription;
	private resizeSubscription?: Subscription;
	private profilePhotoSubscription?: Subscription;
	private imageLoaded = false;
	private loadRequested = false;

	// ============================================
	// Static Cache System
	// ============================================
	/**
	 * Static cache of profile photo URLs
	 * Key: employeeId, Value: { url, timestamp }
	 */
	private static photoCache = new Map<number, CacheEntry>();

	/**
	 * Static cache of original photo URLs (for modal)
	 * Key: employeeId, Value: { url, timestamp }
	 */
	private static originalPhotoCache = new Map<number, CacheEntry>();

	/**
	 * Map of in-progress HTTP requests to avoid duplicates
	 * Key: employeeId, Value: Observable in progress
	 */
	private static pendingRequests = new Map<number, Observable<{ url: string | null }>>();

	/**
	 * Map of in-progress HTTP requests for original photos
	 * Key: employeeId, Value: Observable in progress
	 */
	private static pendingOriginalRequests = new Map<number, Observable<{ url: string | null }>>();

	/**
	 * Cache expiration time in milliseconds (default: 30 minutes)
	 */
	private static CACHE_EXPIRATION_TIME = 30 * 60 * 1000; // 30 minutes

	/**
	 * Load original image without transformations for preview (with cache)
	 */
	private loadOriginalImage(): void {
		const id = this.userId || this.employeeId;
		if (!id) return;

		const numericId = Number(id);
		if (Number.isNaN(numericId)) {
			console.warn(`⚠️ Avatar: invalid userId/employeeId in loadOriginalImage: ${id}`);
			return;
		}

		// For now, use the same image as fullImageUrl (no separate original)
		// In the future, we could fetch a higher quality version if needed
		this.originalImageUrl = this.fullImageUrl;
		this.cdr.markForCheck();
	}

	/**
	 * Public static method to clear cache for a specific employee
	 * Useful when updating an employee's profile photo
	 * @param employeeId ID of the employee whose cache should be cleared
	 */
	public static clearCache(employeeId: number): void {
		AvatarComponent.photoCache.delete(employeeId);
		AvatarComponent.originalPhotoCache.delete(employeeId);
		AvatarComponent.pendingRequests.delete(employeeId);
		AvatarComponent.pendingOriginalRequests.delete(employeeId);
	}

	/**
	 * Public static method to clear all cache
	 * Useful to force reload of all images
	 */
	public static clearAllCache(): void {
		AvatarComponent.photoCache.clear();
		AvatarComponent.originalPhotoCache.clear();
		AvatarComponent.pendingRequests.clear();
		AvatarComponent.pendingOriginalRequests.clear();
	}

	/**
	 * Public static method to get cache statistics
	 */
	public static getCacheStats(): {
		photoCacheSize: number;
		originalPhotoCacheSize: number;
		pendingRequestsCount: number;
		pendingOriginalRequestsCount: number;
	} {
		return {
			photoCacheSize: AvatarComponent.photoCache.size,
			originalPhotoCacheSize: AvatarComponent.originalPhotoCache.size,
			pendingRequestsCount: AvatarComponent.pendingRequests.size,
			pendingOriginalRequestsCount: AvatarComponent.pendingOriginalRequests.size,
		};
	}

	ngOnInit(): void {
		// Subscribe to profile photo updates for the current user
		const currentUserId = this.currentUserService.getUserId();
		const id = this.userId || this.employeeId;

		// Only subscribe if this avatar is displaying the current user
		if (id && currentUserId === Number(id)) {
			this.profilePhotoSubscription = this.currentUserService.profilePhotoUpdated$.subscribe(() => {
				// Get the updated photo URL from the service
				const newPhotoUrl = this.currentUserService.getProfilePhotoUrl();
				this.fullImageUrl = newPhotoUrl || undefined;
				this.originalImageUrl = this.fullImageUrl;
				this.photoResolved = true;
				this.cdr.markForCheck();
			});
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		// If employeeId, userId or imageUrl changes, reset state to allow reload
		if (changes['employeeId'] || changes['userId'] || changes['imageUrl']) {
			// Only reset if value actually changed (not on first load)
			const employeeIdChanged =
				changes['employeeId'] &&
				!changes['employeeId'].firstChange &&
				changes['employeeId'].previousValue !== changes['employeeId'].currentValue;
			const userIdChanged =
				changes['userId'] &&
				!changes['userId'].firstChange &&
				changes['userId'].previousValue !== changes['userId'].currentValue;
			const imageUrlChanged =
				changes['imageUrl'] &&
				!changes['imageUrl'].firstChange &&
				changes['imageUrl'].previousValue !== changes['imageUrl'].currentValue;

			if (employeeIdChanged || userIdChanged || imageUrlChanged) {
				this.imageLoaded = false;
				this.loadRequested = false;
				this.photoResolved = false;
				this.fullImageUrl = undefined;
				this.originalImageUrl = undefined;
				// Clear previous observers
				this.cleanup();

				// If component is initialized, reload image
				if (this.avatarContainer?.nativeElement) {
					if (this.lazyLoad) {
						// Restart lazy loading process
						this.initWithMultipleStrategies();
					} else {
						// Load immediately if lazy loading is disabled
						this.loadImage();
					}
				}
			}
		}
	}

	ngAfterViewInit() {
		// If there is no userId or imageUrl, there is nothing to load - show initials immediately
		const hasImageSource = this.userId || this.employeeId || this.imageUrl;
		if (!hasImageSource) {
			this.fullImageUrl = undefined;
			this.imageLoaded = true;
			this.photoResolved = true;
			this.cdr.markForCheck();
			return;
		}

		// For lazy loading, we need to wait for DOM and check visibility
		if (this.lazyLoad) {
			// Use multiple strategies to ensure we catch the element
			this.initWithMultipleStrategies();
		} else {
			// If lazy loading is disabled, load immediately but after current cycle
			// to avoid ExpressionChangedAfterItHasBeenCheckedError
			setTimeout(() => {
				this.loadImage();
			}, 0);
		}
	}

	private initWithMultipleStrategies(): void {
		// Single strategy: immediate load with requestAnimationFrame
		this.ngZone.runOutsideAngular(() => {
			requestAnimationFrame(() => {
				this.tryLoadImage();
			});
		});
	}

	private tryLoadImage(): void {
		if (this.loadRequested) return;

		const checkElement = () => {
			if (this.loadRequested) return;

			if (!this.avatarContainer?.nativeElement) {
				// If element not found, load anyway
				this.requestImageLoad();
				return;
			}

			const element = this.avatarContainer.nativeElement;

			// Check visibility: if visible load, otherwise setup observer
			if (this.checkVisibility(element)) {
				this.requestImageLoad();
			} else {
				// Set up lazy loading only if not visible
				this.setupIntersectionObserver(element);
				this.setupEventListeners(element);
			}
		};

		checkElement();
	}

	private checkVisibility(element: HTMLElement): boolean {
		try {
			// Fast check: if element is not in DOM, skip expensive operations
			if (!element.offsetParent && element.offsetWidth === 0 && element.offsetHeight === 0) {
				return false;
			}

			const rect = element.getBoundingClientRect();
			const windowHeight = window.innerHeight || document.documentElement.clientHeight;
			const windowWidth = window.innerWidth || document.documentElement.clientWidth;

			// Check visibility with optimized margin
			const isInViewport =
				rect.top < windowHeight + 300 &&
				rect.bottom > -300 &&
				rect.left < windowWidth + 300 &&
				rect.right > -300;

			// Verify element has valid dimensions
			const hasDimensions = rect.width >= 0 && rect.height >= 0;

			// Check computed style only when potentially visible
			if (!isInViewport || !hasDimensions) {
				return false;
			}

			// Check computed style after viewport validation
			const style = window.getComputedStyle(element);
			const isDisplayed =
				style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0;

			return isDisplayed;
		} catch (_error) {
			// If check fails, assume visible to be safe - better to load than miss
			return true;
		}
	}

	private setupIntersectionObserver(element: HTMLElement): void {
		if (this.loadRequested) return;

		try {
			this.intersectionObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting && !this.loadRequested) {
							this.ngZone.run(() => {
								this.requestImageLoad();
							});
							this.cleanup();
						}
					});
				},
				{
					rootMargin: '200px',
					threshold: 0.01,
				},
			);

			this.intersectionObserver.observe(element);
		} catch (error) {
			console.error('Error setting up Intersection Observer:', error);
			// Fallback: check visibility and load if visible
			if (this.checkVisibility(element)) {
				this.requestImageLoad();
			}
		}
	}

	private setupEventListeners(element: HTMLElement): void {
		if (this.loadRequested) return;

		// Simplified: only use scroll events, no resize
		this.scrollSubscription = this.scrollVisibilityService.scroll$.subscribe(() => {
			if (this.loadRequested) return;
			// Run visibility check outside Angular zone
			this.ngZone.runOutsideAngular(() => {
				if (this.checkVisibility(element)) {
					this.ngZone.run(() => {
						this.requestImageLoad();
						this.cleanup();
					});
				}
			});
		});
	}

	private requestImageLoad(): void {
		if (this.loadRequested) return;
		this.loadRequested = true;

		// Enqueue load - no need to run in zone, the queue handles it
		this.imageLoadQueue.enqueueLoad(() => {
			this.loadImage();
			// Force change detection after loading
			this.cdr.markForCheck();
		});
	}

	private cleanup(): void {
		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect();
			this.intersectionObserver = undefined;
		}
		if (this.scrollSubscription) {
			this.scrollSubscription.unsubscribe();
			this.scrollSubscription = undefined;
		}
		if (this.resizeSubscription) {
			this.resizeSubscription.unsubscribe();
			this.resizeSubscription = undefined;
		}
	}

	ngOnDestroy() {
		this.cleanup();
		if (this.profilePhotoSubscription) {
			this.profilePhotoSubscription.unsubscribe();
			this.profilePhotoSubscription = undefined;
		}
	}

	/**
	 * Checks if a cache entry is valid (not expired)
	 */
	private static isCacheValid(entry: CacheEntry): boolean {
		return Date.now() - entry.timestamp < AvatarComponent.CACHE_EXPIRATION_TIME;
	}

	/**
	 * Marks the photo fetch as resolved (success, failure or no-source) and
	 * triggers change detection. Use after every assignment to fullImageUrl
	 * inside async paths so the template can swap the skeleton placeholder
	 * for the image / initials.
	 */
	private resolvePhoto(): void {
		this.photoResolved = true;
		this.cdr.markForCheck();
	}

	/**
	 * Gets the photo URL from cache or makes an HTTP request
	 */
	private loadImage(): void {
		if (this.imageLoaded) return;
		this.imageLoaded = true;

		// Use userId first, fallback to employeeId for backward compatibility
		const id = this.userId || this.employeeId;

		// If userId or employeeId exists, fetch URL from backend (with cache)
		if (id) {
			const numericId = Number(id);
			if (Number.isNaN(numericId)) {
				console.warn(`⚠️ Avatar: invalid userId/employeeId: ${id} (type: ${typeof id})`);
				return;
			}

			// 0. Check if this is the current user - use cached photo from login
			const currentUserId = this.currentUserService.getUserId();
			if (currentUserId === numericId) {
				const cachedPhotoUrl = this.currentUserService.getProfilePhotoUrl();
				if (cachedPhotoUrl !== undefined) {
					this.fullImageUrl = cachedPhotoUrl || undefined;
					this.loadOriginalImage();
					this.resolvePhoto();
					return;
				}
			}

			// 1. Check if in cache and valid
			const cachedEntry = AvatarComponent.photoCache.get(numericId);
			if (cachedEntry) {
				const isValid = AvatarComponent.isCacheValid(cachedEntry);

				if (isValid) {
					this.fullImageUrl = cachedEntry.url || undefined;
					// Load original image for preview
					this.loadOriginalImage();
					this.resolvePhoto();
					return;
				} else {
					// Remove expired cache entry
					AvatarComponent.photoCache.delete(numericId);
				}
			}

			// 2. Check if there's a request in progress
			const pendingRequest = AvatarComponent.pendingRequests.get(numericId);
			if (pendingRequest) {
				pendingRequest.subscribe({
					next: (response) => {
						this.fullImageUrl = response.url || undefined;
						// Load original image for preview
						this.loadOriginalImage();
						this.resolvePhoto();
					},
					error: (err) => {
						console.error(`❌ Error in pending request for user ${numericId}:`, err);
						this.fullImageUrl = undefined;
						this.resolvePhoto();
					},
				});
				return;
			}

			// 3. Create new request and cache it
			const request$ = this.http
				.get<{ url: string | null }>(`${environment.pathBackend}/users/${numericId}/profile-photo`)
				.pipe(
					tap((response) => {
						// Save to cache
						const cacheEntry = {
							url: response.url,
							timestamp: Date.now(),
						};
						AvatarComponent.photoCache.set(numericId, cacheEntry);
						// Clear pending request
						AvatarComponent.pendingRequests.delete(numericId);
					}),
					shareReplay(1), // Share result between multiple subscribers
				);

			// Save as pending request
			AvatarComponent.pendingRequests.set(numericId, request$);

			// Subscribe to update component
			request$.subscribe({
				next: (response) => {
					this.fullImageUrl = response.url || undefined;
					// Load original image for preview
					this.loadOriginalImage();
					this.resolvePhoto();
				},
				error: (err) => {
					// Only log errors that are not 404 (missing photo is expected)
					if (err?.status !== 404) {
						console.error(`❌ Error fetching profile photo for user ${numericId}:`, err);
					}
					this.fullImageUrl = undefined;
					// Save error in cache to avoid immediate retries
					const errorCacheEntry = {
						url: null,
						timestamp: Date.now(),
					};
					AvatarComponent.photoCache.set(numericId, errorCacheEntry);
					// Clear pending request
					AvatarComponent.pendingRequests.delete(numericId);
					this.resolvePhoto();
				},
			});
		}
		// If it's a full URL (http/https), use it directly
		else if (this.imageUrl?.startsWith('http')) {
			this.fullImageUrl = this.imageUrl;
			this.originalImageUrl = this.imageUrl; // For direct URLs, use same URL
			this.resolvePhoto();
		}
		// If imageUrl is null or undefined, no image (initials will be shown)
		else {
			this.fullImageUrl = undefined;
			this.resolvePhoto();
		}
	}

	getInitials(name: string): string {
		// Letters/digits only: names prefixed with markers like "[TM]" must not
		// yield initials such as "[A".
		return name
			.split(' ')
			.map((word) => word.replace(/[^\p{L}\p{N}]/gu, '')[0])
			.filter(Boolean)
			.join('')
			.toUpperCase()
			.slice(0, 2);
	}

	// Deterministic colour per name so the initials avatars aren't all dark.
	private static readonly AVATAR_COLORS = [
		'#1D9E75',
		'#D85A30',
		'#378ADD',
		'#7F77DD',
		'#D4537E',
		'#0884B0',
		'#BA7517',
		'#16A765',
		'#5E2FD9',
		'#0E8A8F',
	];

	getAvatarColor(name: string): string {
		const colors = AvatarComponent.AVATAR_COLORS;
		if (!name) {
			return colors[0];
		}
		let hash = 0;
		for (let i = 0; i < name.length; i++) {
			hash = (hash * 31 + name.charCodeAt(i)) | 0;
		}
		return colors[Math.abs(hash) % colors.length];
	}
}
